import { getPassportImage } from "api/apiService";
import React, { useEffect, useState, useRef } from "react";
import Info from "../assets/icons/ic_info.svg";
import { useSnackBar } from "../components/snackBar/SnackBarContext"; // adjust the path as needed

const maxSize = 15; // In MB
const acceptedFormats = ["image/jpeg", "image/png", "image/webp"];

const FileUploader = ({
  formData,
  handleChange,
  formErrors,
  handleFileChange,
}) => {
  const [previewUrl, setPreviewUrl] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const { showSnackbar } = useSnackBar();

  useEffect(() => {
    const getPassportRes = async () => {
      try {
        const blob = await getPassportImage(formData.personalId);
        if (blob) {
          const imageUrl = URL.createObjectURL(blob);
          setPreviewUrl(imageUrl);
          const file = new File([blob], formData.firstName, {
            type: blob.type,
          });
          handleChange({ target: { name: "fileCopy", value: file.name } });
          handleChange({ target: { name: "file", value: file } });
        }
      } catch (err) {
        console.error("Error in fetching image", err);
      }
    };
    if (formData.personalId) getPassportRes();
  }, [formData.personalId]);

  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleFile = (file) => {
    if (!file) return;

    if (!acceptedFormats.includes(file.type)) {
      // alert("Unsupported file type");
      showSnackbar("warning", "Unsupported file type");
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      // alert(`File exceeds ${maxSize} MB limit`);
      showSnackbar("warning", `File exceeds ${maxSize} MB limit`);

      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
      setUploadedFile(file);
      setIsEditing(false);
    };
    reader.readAsDataURL(file);
    handleChange({ target: { name: "fileCopy", value: file.name } });
    handleChange({ target: { name: "file", value: file } });
    handleFileChange(file);
  };

  const handleViewClick = () => {
    setShowPreview(true);
    /*const newWindow = window.open("", "_blank");
        newWindow.document.write(`<img src="${previewUrl}" alt="Preview Image" />`)*/
  };

  return (
    <div className="w-full">
      {previewUrl ? (
        <div
          className="relative group w-full h-40 rounded-lg overflow-hidden border-2 border-gray-200 mt-2"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            src={previewUrl}
            alt="Uploaded Preview"
            className="w-full h-full object-cover"
          />

          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-4 transition-opacity duration-200 backdrop-blur-sm ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
          >
            <button
              onClick={handleViewClick}
              className="flex items-center gap-2 px-3 py-1 text-xs bg-white rounded-md hover:bg-gray-100"
            >
              <img src="/ic_viewfile.svg" alt="View" width={16} height={16} />
              View
            </button>
            <button
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={handleUploadClick} // ⬅️ This opens file manager directly
              className="flex items-center gap-2 px-3 py-1 text-xs bg-white rounded-md hover:bg-gray-100"
            >
              <img
                src="/ic_imguploadgrey.svg"
                alt="Edit"
                width={18}
                height={18}
              />
              Edit
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(",")}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleUploadClick}
          className={`relative w-full h-64 border-2 border-dashed rounded-lg cursor-pointer
            flex flex-col items-center justify-center gap-4 p-6
            transition-all duration-200 hover:bg-gray-50
            ${
              isDragOver
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
        >
          <div className="w-16 h-16 flex items-center justify-center">
            <img
              src="/ic_imguploadblue.svg"
              alt="Upload"
              width={32}
              height={32}
            />
          </div>
          <p className="text-xs text-netural7 text-center">
            Please upload passport. Supported formats:{" "}
            {acceptedFormats
              .map((f) => f.split("/")[1].toUpperCase())
              .join(" and ")}
            .
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(",")}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}

      {formErrors?.file && (
        <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
          <img src={Info} alt="info" />
          {formErrors.file}
        </div>
      )}

      {showPreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
          onClick={() => setShowPreview(false)}
        >
          <div className="relative w-[90vw] h-[90vh] bg-transparent rounded-lg shadow-lg flex items-center justify-center">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-2 right-2 text-white text-lg z-10"
            >
              &times;
            </button>
            <img
              src={previewUrl}
              alt="Full Preview"
              className="object-contain w-full h-full "
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
